import { useMemo } from 'react';

import { overviewDays } from '@app/constants/app';
import { addDays, dayKey, fullDate, getCurrentSprint, getPreviousSprint } from '@app/utils';
import { useListSprints } from '@data/hooks/sprints';

/** Far enough back to hold this sprint and the one before it, without asking for the whole history. */
const lookbackDays = 60;

export interface ReportingWindow {
  /** Midnight UTC on the first day of the previous sprint. */
  from: Date;
  /** Midnight UTC today. A day you cannot report yet is not a day you owe. */
  to: Date;
}

/** Midnight UTC on a `yyyy-mm-dd` day, which is the calendar the day keys are counted on. */
const utcDay = (day: string): Date => new Date(`${day}T00:00:00.000Z`);

/** The day `days` before this one, back as a `yyyy-mm-dd` key. */
const daysBefore = (day: string, days: number): string => fullDate(addDays(utcDay(day), -days));

/**
 * The days "To report" answers for: this sprint and the one before it, up to today.
 *
 * The nav badge, the To report list and the dotted days in the Phill dialog all read this and none of
 * them works its own dates out. A window derived in three places is a window that can disagree with
 * itself — the same reason it is a single value on the wire rather than something the API decides.
 *
 * Both ends are anchored to midnight UTC so every caller asks the identical question and shares one
 * answer, rather than each sending the instant it happened to mount at.
 *
 * `undefined` while the sprints are still loading, so a caller waits instead of asking about a window
 * that is about to change under it. If they arrive and there are none to go on, it falls back to the
 * rolling `overviewDays` window: a badge that never appears is worse than one measured a few days out.
 */
export const useReportingWindow = (): ReportingWindow | undefined => {
  const today = dayKey(new Date());

  const params = useMemo(() => ({ from: daysBefore(today, lookbackDays), to: today, limit: 100 }), [today]);
  const { data: sprints, isPending } = useListSprints(params);

  return useMemo(() => {
    if (isPending) {
      return undefined;
    }

    const items = sprints?.items ?? [];
    const current = getCurrentSprint(items);
    // The previous sprint when there is one, so the window covers both. On the very first sprint there
    // is nothing before it and the current one is the whole window.
    const start = getPreviousSprint(items, current)?.startDate ?? current?.startDate;

    return {
      from: utcDay(start ? fullDate(start) : daysBefore(today, overviewDays)),
      to: utcDay(today),
    };
  }, [isPending, sprints, today]);
};
